import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { AlertTriangle, Trash2, Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const DeleteAccountModal = ({ isOpen, onClose, user }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [deleteData, setDeleteData] = useState({
    password: '',
    confirmation: '',
    twoFaCode: ''
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  const handleAccountDeletion = async (e) => {
    e.preventDefault();
    
    if (deleteData.confirmation !== 'DELETE MY ACCOUNT') {
      toast.error('Please type "DELETE MY ACCOUNT" to confirm');
      return;
    }

    if (!deleteData.password) {
      toast.error('Please enter your password');
      return;
    }

    if (user?.two_fa_enabled && !deleteData.twoFaCode) {
      toast.error('Two-factor authentication code required');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.delete(
        `${BACKEND_URL}/api/settings/account`,
        {
          data: {
            password: deleteData.password,
            confirmation_text: deleteData.confirmation,
            two_fa_code: deleteData.twoFaCode || null
          },
          withCredentials: true
        }
      );

      toast.success('Account deletion scheduled. You have 30 days to cancel if you change your mind.');
      
      // Redirect to home page after successful deletion
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);

    } catch (error) {
      console.error('Account deletion error:', error);
      const errorMessage = error.response?.data?.detail || 'Account deletion failed';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            <span>Delete Account</span>
          </DialogTitle>
          <DialogDescription>
            This action will permanently delete your account and all associated data after a 30-day grace period.
          </DialogDescription>
        </DialogHeader>

        <div className="my-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-800">
              <p className="font-semibold mb-2">This will permanently delete:</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Your profile and account information</li>
                <li>All groups, expenses, and transaction history</li>
                <li>Payment methods and billing information</li>
                <li>All settings and preferences</li>
              </ul>
              <p className="mt-3 font-medium">
                You have 30 days to cancel by signing back in.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleAccountDeletion} className="space-y-4">
          <div>
            <Label htmlFor="delete_password">Current Password</Label>
            <Input
              id="delete_password"
              type="password"
              value={deleteData.password}
              onChange={(e) => setDeleteData({ ...deleteData, password: e.target.value })}
              placeholder="Enter your current password"
              className="mt-1"
              required
            />
          </div>

          {user?.two_fa_enabled && (
            <div>
              <Label htmlFor="delete_2fa" className="flex items-center space-x-2">
                <Shield className="w-4 h-4 text-gray-600" />
                <span>Two-Factor Authentication Code</span>
              </Label>
              <Input
                id="delete_2fa"
                type="text"
                value={deleteData.twoFaCode}
                onChange={(e) => setDeleteData({ ...deleteData, twoFaCode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                placeholder="000000"
                className="mt-1 text-center tracking-widest font-mono"
                maxLength={6}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter the 6-digit code from your authenticator app
              </p>
            </div>
          )}

          <div>
            <Label htmlFor="delete_confirmation">
              Type "DELETE MY ACCOUNT" to confirm
            </Label>
            <Input
              id="delete_confirmation"
              type="text"
              value={deleteData.confirmation}
              onChange={(e) => setDeleteData({ ...deleteData, confirmation: e.target.value })}
              placeholder="DELETE MY ACCOUNT"
              className="mt-1 font-mono"
              required
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || deleteData.confirmation !== 'DELETE MY ACCOUNT'}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete Account
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteAccountModal;
